import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layers, Percent } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { BotonesDeDescarga, descargarInforme, type Formato } from '@/shared/ui/Descargas';
import { enDinero } from '@/shared/ui/graficos';

interface FilaDeServicio {
  periodo: string;
  servicio: string;
  nombre: string;
  facturas: number;
  lineas: number;
  subtotal: number;
  descuento: number;
  total: number;
}

interface Facturado {
  filas: FilaDeServicio[];
  subtotal: number;
  descuento: number;
  total: number;
}

interface Exoneracion {
  periodo: string;
  tipo: string;
  lineas: number;
  cuentas: number;
  subtotal: number;
  descuento: number;
  total: number;
}

/**
 * Lo facturado por servicio —Art. 8, cada literal del Art. 9, servicios especiales— y lo que
 * se dejó de cobrar por exoneraciones.
 *
 * <p>Es lo FACTURADO, no lo cobrado: el informe de recaudación por concepto responde la otra
 * pregunta. Y el descuento de aquí es el que ya se aplicó en facturas emitidas, no la
 * proyección del reporte de exoneraciones del tarifario.
 */
export function PanelDeServicios({ desde, hasta }: { desde: string; hasta: string }) {
  const [servicio, setServicio] = useState('');
  const [bajando, setBajando] = useState<Formato | null>(null);

  const consulta = { ...(desde ? { desde } : {}), ...(hasta ? { hasta } : {}) };

  /**
   * La descarga lleva los MISMOS filtros que la pantalla.
   *
   * <p>Si no los llevara, el papel saldria con todos los servicios mientras la pantalla ensena
   * uno, y nadie lo notaria hasta que alguien discuta el total en una reunion.
   */
  async function descargar(informe: string, queEs: string, formato: Formato, conServicio: boolean) {
    setBajando(formato);
    const parametros = new URLSearchParams({
      ...consulta,
      ...(conServicio && servicio ? { servicio } : {}),
      formato,
    });
    await descargarInforme(
      `/informes/facturacion/${informe}/descarga?${parametros.toString()}`,
      `${informe}${desde ? `-${desde}` : ''}.${formato}`,
      queEs,
    );
    setBajando(null);
  }

  const servicios = useQuery({
    queryKey: ['cartera-servicios'],
    queryFn: () => api.get<{ codigo: string; nombre: string }[]>('/cartera/servicios'),
  });

  const facturado = useQuery({
    queryKey: ['facturado-por-servicio', consulta, servicio],
    queryFn: () =>
      api.get<Facturado>('/informes/facturacion/facturado-por-servicio', {
        query: { ...consulta, ...(servicio ? { servicio } : {}) },
      }),
  });

  const exoneraciones = useQuery({
    queryKey: ['exoneraciones-aplicadas', consulta],
    queryFn: () =>
      api.get<Exoneracion[]>('/informes/facturacion/exoneraciones-aplicadas', {
        query: consulta,
      }),
  });

  if (facturado.isLoading) {
    return <Cargando titulo="Cargando" detalle="sumando lo facturado por servicio" />;
  }

  const datos = facturado.data;
  if (!datos || datos.filas.length === 0) {
    return (
      <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
        No se facturó nada en ese rango
      </p>
    );
  }

  const conDescuento = (exoneraciones.data ?? []).filter((e) => e.descuento > 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Dato rotulo="Facturado bruto" valor={enDinero(datos.subtotal)} />
        <Dato rotulo="Exonerado" valor={enDinero(datos.descuento)} ambar />
        <Dato rotulo="Emitido" valor={enDinero(datos.total)} />
      </div>

      <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-armazon-100 bg-armazon-50/60 px-5 py-3">
          <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-armazon-800">
            <Layers className="size-4 text-armazon-500" aria-hidden />
            Facturado por servicio
          </h3>
          <span className="flex items-center gap-2">
            <BotonesDeDescarga
              bajando={bajando}
              alDescargar={(f) =>
                void descargar('facturado-por-servicio', 'Facturado por servicio', f, true)
              }
              pdf="Descargar en PDF, con el filtro que está aplicado"
              xlsx="Descargar en Excel, con el filtro que está aplicado"
            />
            <select
              value={servicio}
              onChange={(e) => setServicio(e.target.value)}
              className="h-9 rounded-lg border border-armazon-200 bg-white px-3 text-xs text-armazon-900 outline-none focus:border-marca-400"
            >
              <option value="">Todos los servicios</option>
              {(servicios.data ?? []).map((s) => (
                <option key={s.codigo} value={s.codigo}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </span>
        </header>

        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-armazon-100 text-[11px] uppercase tracking-wide text-armazon-500">
              <th className="px-5 py-2 text-left">Período</th>
              <th className="px-5 py-2 text-left">Servicio</th>
              <th className="px-5 py-2 text-right">Facturas</th>
              <th className="px-5 py-2 text-right">Bruto</th>
              <th className="px-5 py-2 text-right">Exonerado</th>
              <th className="px-5 py-2 text-right">Emitido</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-armazon-100">
            {datos.filas.map((f) => (
              <tr key={`${f.periodo}-${f.servicio}`}>
                <td className="importe px-5 py-2 text-armazon-700">{f.periodo}</td>
                <td className="px-5 py-2 uppercase text-armazon-700">{f.nombre}</td>
                <td className="importe px-5 py-2 text-right text-armazon-600">{f.facturas}</td>
                <td className="importe px-5 py-2 text-right text-armazon-700">
                  {enDinero(f.subtotal)}
                </td>
                <td className="importe px-5 py-2 text-right text-amber-700">
                  {f.descuento > 0 ? enDinero(f.descuento) : '—'}
                </td>
                <td className="importe px-5 py-2 text-right font-semibold text-armazon-900">
                  {enDinero(f.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-armazon-100 bg-armazon-50/60 px-5 py-3">
          <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-armazon-800">
            <Percent className="size-4 text-armazon-500" aria-hidden />
            Exoneraciones aplicadas
          </h3>
          {conDescuento.length > 0 && (
            <BotonesDeDescarga
              bajando={bajando}
              alDescargar={(f) =>
                void descargar('exoneraciones-aplicadas', 'Exoneraciones aplicadas', f, false)
              }
              pdf="Descargar en PDF"
              xlsx="Descargar en Excel"
            />
          )}
        </header>

        {conDescuento.length === 0 ? (
          <p className="px-5 py-8 text-center text-xs uppercase leading-relaxed text-armazon-400">
            Ninguna factura emitida en ese rango llevó exoneración. Lo que el tarifario muestra es
            lo que se dejará de cobrar en la próxima emisión, que es otra cosa.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-armazon-100 text-[11px] uppercase tracking-wide text-armazon-500">
                <th className="px-5 py-2 text-left">Período</th>
                <th className="px-5 py-2 text-left">Tipo</th>
                <th className="px-5 py-2 text-right">Predios</th>
                <th className="px-5 py-2 text-right">Bruto</th>
                <th className="px-5 py-2 text-right">Exonerado</th>
                <th className="px-5 py-2 text-right">Emitido</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-armazon-100">
              {conDescuento.map((e) => (
                <tr key={`${e.periodo}-${e.tipo}`}>
                  <td className="importe px-5 py-2 text-armazon-700">{e.periodo}</td>
                  <td className="px-5 py-2 uppercase text-armazon-700">{e.tipo}</td>
                  <td className="importe px-5 py-2 text-right text-armazon-600">{e.cuentas}</td>
                  <td className="importe px-5 py-2 text-right text-armazon-700">
                    {enDinero(e.subtotal)}
                  </td>
                  <td className="importe px-5 py-2 text-right text-amber-700">
                    {enDinero(e.descuento)}
                  </td>
                  <td className="importe px-5 py-2 text-right font-semibold text-armazon-900">
                    {enDinero(e.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Dato({ rotulo, valor, ambar }: { rotulo: string; valor: string; ambar?: boolean }) {
  return (
    <div className="rounded-2xl border border-armazon-200 bg-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-armazon-500">
        {rotulo}
      </p>
      <p
        className={`importe mt-1 text-xl font-bold ${ambar ? 'text-amber-700' : 'text-armazon-900'}`}
      >
        {valor}
      </p>
    </div>
  );
}
